/**
 * Session context utilities — telemetria FE per popolare colonne pre-esistenti
 * in `events_search_requested` (session_id, device_type, source_channel).
 *
 * - `session_id` è persistito in `sessionStorage` (vive per la durata della
 *   tab; resetta su close-tab). Usiamo `sessionStorage` invece di Redux/persist
 *   perché la session è semanticamente legata al tab del browser, non al login.
 *
 * - `device_type` è derivato sincronamente da `navigator.userAgent`. UA parsing
 *   è notoriamente inaffidabile ma sufficiente per analytics aggregate.
 *
 * - `source_channel` è catturato UNA volta al primo mount dell'app (in
 *   `main.tsx`, PRIMA di StrictMode) e persistito in sessionStorage. Le
 *   chiamate successive leggono solo da storage — necessario perché
 *   `document.referrer` e `utm_*` sono validi solo al primo landing,
 *   diventano stale dopo navigation in-app.
 */

const SESSION_ID_KEY = "herika.session_id";
export const SOURCE_CHANNEL_KEY = "herika.source_channel";

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Returns a stable session UUID for the current browser tab.
 * Creates one on first call and persists in sessionStorage.
 */
export function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing && UUID_REGEX.test(existing)) return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(SESSION_ID_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage può fallire in private browsing molto restrittivo →
    // fallback in-memory (perdiamo la persistenza ma non crashiamo)
    return crypto.randomUUID();
  }
}

/**
 * Heuristic User-Agent → device_type. Sufficient for analytics aggregate.
 * UA-CH (Client Hints) sarebbe il futuro, ma non è ancora ubiquitous.
 */
export function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod|BlackBerry|Opera Mini|IEMobile/i.test(ua)) {
    return "mobile";
  }
  return "desktop";
}

/**
 * Returns the source channel captured at first mount (see captureSourceChannel
 * in main.tsx). Returns undefined if never captured (direct navigation without
 * UTM or referrer).
 */
export function getSourceChannel(): string | undefined {
  try {
    const v = sessionStorage.getItem(SOURCE_CHANNEL_KEY);
    return v && v.length > 0 ? v : undefined;
  } catch {
    return undefined;
  }
}

/**
 * One-shot capture of UTM source / referrer at app boot. Idempotent: only
 * writes the first time (subsequent calls early-return). Safe under React
 * StrictMode double-effect because we guard on the storage key.
 *
 * Call from `main.tsx` BEFORE `<StrictMode>` — at that point the URL still
 * reflects the landing page (UTM params present, document.referrer valid).
 */
export function captureSourceChannelOnce(): void {
  try {
    if (sessionStorage.getItem(SOURCE_CHANNEL_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const utm = params.get("utm_source");
    if (utm) {
      sessionStorage.setItem(SOURCE_CHANNEL_KEY, utm.slice(0, 50));
      return;
    }
    if (document.referrer) {
      try {
        const host = new URL(document.referrer).hostname;
        if (host && host !== window.location.hostname) {
          sessionStorage.setItem(SOURCE_CHANNEL_KEY, host.slice(0, 50));
        }
      } catch {
        // malformed referrer → skip
      }
    }
  } catch {
    // sessionStorage non disponibile → skip, source_channel resterà undefined
  }
}
